import type { NxRichTextImageWrap } from '../RichTextToolbar';

/**
 * Editing operations on the frame's document. The browser's `execCommand`
 * does what it does well (marks, lists, alignment, colours); the rest —
 * point sizes, line spacing, tables, quotes, pictures — are DOM operations
 * of their own, because `execCommand` knows only seven font sizes and no
 * tables at all.
 */

const BLOCK_SELECTOR = 'p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre, td, th';

export function exec(doc: Document, command: string, value?: string, css = false): boolean {
  doc.execCommand('styleWithCSS', false, String(css));
  return doc.execCommand(command, false, value);
}

function rangeOf(doc: Document): Range | undefined {
  const selection = doc.getSelection();
  return selection && selection.rangeCount ? selection.getRangeAt(0) : undefined;
}

function elementOf(node: Node | null | undefined): Element | null {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
}

/** The element the caret (or the start of the selection) sits in. */
export function caretElement(doc: Document): Element | null {
  const range = rangeOf(doc);
  if (!range) return null;
  const node = range.startContainer;
  if (
    node.nodeType === Node.ELEMENT_NODE &&
    node.childNodes[range.startOffset]?.nodeType === Node.ELEMENT_NODE
  ) {
    return node.childNodes[range.startOffset] as Element;
  }
  return elementOf(node);
}

export function closestIn(doc: Document, selector: string): HTMLElement | null {
  const element = caretElement(doc)?.closest<HTMLElement>(selector) ?? null;
  return element && doc.body.contains(element) && element !== doc.body ? element : null;
}

/**
 * The block elements the selection touches, innermost first. Text sitting
 * straight in `<body>` gets a `<p>` around it so it has a block to style.
 */
export function blocksInSelection(doc: Document): HTMLElement[] {
  const range = rangeOf(doc);
  if (!range) return [];
  const blocks = new Set<HTMLElement>();
  const add = (node: Node) => {
    const block = elementOf(node)?.closest<HTMLElement>(BLOCK_SELECTOR);
    if (block && doc.body.contains(block)) blocks.add(block);
  };
  if (range.collapsed) {
    add(range.startContainer);
  } else {
    const walker = doc.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT);
    let node: Node | null =
      walker.currentNode.nodeType === Node.TEXT_NODE ? walker.currentNode : walker.nextNode();
    while (node) {
      if (range.intersectsNode(node) && node.textContent?.trim()) add(node);
      node = walker.nextNode();
    }
    if (!blocks.size) add(range.startContainer);
  }
  if (!blocks.size) {
    exec(doc, 'formatBlock', '<p>');
    const block = closestIn(doc, BLOCK_SELECTOR);
    if (block) blocks.add(block);
  }
  // Only the innermost: a table cell inside a div gets the style, not the div.
  return [...blocks].filter(
    (block) => ![...blocks].some((other) => other !== block && block.contains(other))
  );
}

function selectAround(doc: Document, first: Node, last: Node): void {
  const range = doc.createRange();
  range.setStartBefore(first);
  range.setEndAfter(last);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

const SIZE_MARK = 'data-nx-html-editor-size';

/** A size in points; nested sizes inside the selection give way to it. */
export function applyFontSize(doc: Document, points: number): void {
  // A <font size="7"> the text already had is not part of this selection.
  const earlier = [...doc.body.querySelectorAll<HTMLElement>('font[size="7"]')];
  for (const font of earlier) font.setAttribute(SIZE_MARK, '');
  exec(doc, 'fontSize', '7');
  const marked = [...doc.body.querySelectorAll<HTMLElement>('font[size="7"]')].filter(
    (font) => !font.hasAttribute(SIZE_MARK)
  );
  for (const font of earlier) font.removeAttribute(SIZE_MARK);
  if (!marked.length) return;
  const spans = marked.map((font) => {
    font
      .querySelectorAll<HTMLElement>('[style]')
      .forEach((inner) => inner.style.removeProperty('font-size'));
    font.removeAttribute('size');
    // A <font> the browser reused (it carried a face or colour) keeps them.
    const target = font.attributes.length ? font : doc.createElement('span');
    target.style.fontSize = `${points}pt`;
    if (target !== font) {
      target.append(...font.childNodes);
      font.replaceWith(target);
    }
    return target;
  });
  selectAround(doc, spans[0], spans[spans.length - 1]);
}

/** Line spacing in percent of single spacing; `null` drops it. */
export function applyLineSpacing(doc: Document, percent: number | null): void {
  for (const block of blocksInSelection(doc)) {
    if (percent == null) block.style.removeProperty('line-height');
    else block.style.lineHeight = String(((percent / 100) * 1.2).toFixed(2)).replace(/\.?0+$/, '');
    if (!block.getAttribute('style')) block.removeAttribute('style');
  }
}

const CELL_STYLE = 'border: 1px solid #c8c8c8; padding: 4px 8px; vertical-align: top;';

/** An empty table that mail clients render the same; a paragraph follows so typing can go on below it. */
export function tableHtml(rows: number, columns: number): string {
  const cells = `<td style="${CELL_STYLE}"><br></td>`.repeat(columns);
  const body = `<tr>${cells}</tr>`.repeat(rows);
  return `<table style="border-collapse: collapse; width: 100%;"><tbody>${body}</tbody></table><p><br></p>`;
}

function placeCaret(doc: Document, node: Node): void {
  const range = doc.createRange();
  range.selectNodeContents(node);
  range.collapse(true);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function emptyCellLike(doc: Document, cell: HTMLTableCellElement): HTMLTableCellElement {
  const copy = doc.createElement(cell.localName) as HTMLTableCellElement;
  const style = cell.getAttribute('style');
  if (style) copy.setAttribute('style', style);
  copy.innerHTML = '<br>';
  return copy;
}

/** Inserts or deletes the row or the column of the caret's cell. */
export function tableEdit(doc: Document, kind: 'row' | 'column', op: 'insert' | 'delete'): boolean {
  const cell = closestIn(doc, 'td, th') as HTMLTableCellElement | null;
  const row = cell?.parentElement as HTMLTableRowElement | null;
  const table = cell?.closest('table');
  if (!cell || !row || !table) return false;
  // Merged cells make a cell's index no column number: refuse instead of guessing.
  if (hasMergedCells(table)) return false;
  if (kind === 'row') {
    if (op === 'insert') {
      const copy = doc.createElement('tr');
      for (const source of [...row.cells]) {
        const next = emptyCellLike(doc, source);
        if (source.colSpan > 1) next.colSpan = source.colSpan;
        copy.append(next);
      }
      row.after(copy);
      placeCaret(doc, copy.cells[Math.min(cell.cellIndex, copy.cells.length - 1)]);
    } else {
      const next = (row.nextElementSibling ??
        row.previousElementSibling) as HTMLTableRowElement | null;
      row.remove();
      if (!table.querySelector('tr')) table.remove();
      else if (next?.cells.length)
        placeCaret(doc, next.cells[Math.min(cell.cellIndex, next.cells.length - 1)]);
    }
    return true;
  }
  const index = cell.cellIndex;
  const rows = [...table.querySelectorAll('tr')].filter((tr) => tr.closest('table') === table);
  for (const tr of rows) {
    const target = (tr as HTMLTableRowElement).cells[
      Math.min(index, (tr as HTMLTableRowElement).cells.length - 1)
    ];
    if (!target) continue;
    if (op === 'insert') target.after(emptyCellLike(doc, target));
    else target.remove();
  }
  for (const tr of rows) if (!(tr as HTMLTableRowElement).cells.length) tr.remove();
  if (!table.querySelector('td, th')) table.remove();
  else {
    const cells = row.isConnected ? row.cells : undefined;
    if (cells?.length)
      placeCaret(doc, cells[Math.min(op === 'insert' ? index + 1 : index, cells.length - 1)]);
  }
  return true;
}

export function hasMergedCells(table: HTMLTableElement): boolean {
  return [...table.querySelectorAll<HTMLTableCellElement>('td, th')].some(
    (cell) => cell.closest('table') === table && (cell.colSpan > 1 || cell.rowSpan > 1)
  );
}

const INLINE_BREAKERS = new Set(['style', 'script', 'link', 'meta', 'template', 'title']);

/**
 * Wraps text and inline elements that sit straight in <body> into a <div>
 * each run, so a saved text starts with a tag the way stored HTML is
 * recognised (B-DMS activities) and new paragraphs do not glue to loose text.
 * A <div> without style renders like the anonymous block it replaces.
 */
export function wrapLooseInline(body: HTMLElement): void {
  const doc = body.ownerDocument;
  let run: Node[] = [];
  const flush = () => {
    const meaningful = run.some(
      (node) =>
        (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) ||
        node.nodeType === Node.ELEMENT_NODE
    );
    if (meaningful) {
      const div = doc.createElement('div');
      run[0].parentNode?.insertBefore(div, run[0]);
      div.append(...run);
    }
    run = [];
  };
  for (const node of [...body.childNodes]) {
    const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : null;
    const inline =
      node.nodeType === Node.TEXT_NODE ||
      node.nodeType === Node.COMMENT_NODE ||
      (element && !isBlock(element) && !INLINE_BREAKERS.has(element.localName));
    if (inline) run.push(node);
    else flush();
  }
  flush();
}

export const QUOTE_STYLE =
  'margin: 0 0 0 0.8ex; padding-left: 1ex; border-left: 2px solid #c8c8c8;';

/** Puts the selected blocks into a quote, or takes them one quote level out. */
export function quote(doc: Document, delta: 1 | -1): boolean {
  if (delta < 0) {
    const outer = closestIn(doc, 'blockquote');
    if (!outer) return false;
    outer.replaceWith(...outer.childNodes);
    return true;
  }
  const blocks = blocksInSelection(doc).filter(
    (block) => !['TD', 'TH', 'LI'].includes(block.tagName)
  );
  if (!blocks.length) return false;
  // One quote per parent: blocks in two table cells stay in their cells.
  const groups = new Map<ParentNode, HTMLElement[]>();
  for (const block of blocks) {
    const parent = block.parentNode;
    if (!parent) continue;
    groups.set(parent, [...(groups.get(parent) ?? []), block]);
  }
  for (const group of groups.values()) {
    const wrapper = doc.createElement('blockquote');
    wrapper.setAttribute('style', QUOTE_STYLE);
    group[0].before(wrapper);
    for (const block of group) wrapper.append(block);
  }
  return true;
}

export function setImageWrap(image: HTMLImageElement, wrap: NxRichTextImageWrap): void {
  if (wrap === 'inline') {
    image.style.removeProperty('float');
    image.style.removeProperty('margin');
  } else {
    image.style.float = wrap;
    image.style.margin = wrap === 'left' ? '0 12px 8px 0' : '0 0 8px 12px';
  }
  if (!image.getAttribute('style')) image.removeAttribute('style');
}

export function imageWrapOf(image: HTMLImageElement): NxRichTextImageWrap {
  const float = image.style.float || image.getAttribute('align') || '';
  return float === 'left' || float === 'right' ? float : 'inline';
}

/** Links the selection; with a bare caret the address itself becomes the text. */
export function applyLink(doc: Document, href: string): void {
  const link = closestIn(doc, 'a[href]') as HTMLAnchorElement | null;
  const range = rangeOf(doc);
  if (link && range?.collapsed) {
    link.href = href;
    return;
  }
  if (!range || range.collapsed) {
    const anchor = doc.createElement('a');
    anchor.href = href;
    anchor.textContent = href.replace(/^mailto:/i, '');
    insertHtmlAtCaret(doc, anchor.outerHTML);
    return;
  }
  exec(doc, 'createLink', href);
}

export function removeLink(doc: Document): void {
  const link = closestIn(doc, 'a[href]');
  const range = rangeOf(doc);
  if (link && range?.collapsed) {
    link.replaceWith(...link.childNodes);
    return;
  }
  exec(doc, 'unlink');
}

const BLOCK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figure',
  'footer',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'ul',
]);
/** Blocks that may hold only inline content: a block inserted into one splits it. */
const PHRASING_BLOCKS = 'p, h1, h2, h3, h4, h5, h6, pre';

const isBlock = (node: Node) =>
  node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).localName);

function isEmptyBlock(element: Element): boolean {
  return (
    !element.textContent?.replace(/​/g, '').trim() &&
    !element.querySelector('img, table, hr, br + br')
  );
}

/**
 * Inserts HTML at the caret and returns what was inserted. One paragraph
 * merges into the current one, the way pasted text does; blocks (a table, a
 * text block of several paragraphs) split the paragraph at the caret and
 * land between its halves. The browser's `insertHTML` is not used: it wraps
 * the inserted text in spans that copy the surrounding font size.
 */
export function insertHtmlAtCaret(doc: Document, html: string): Node[] {
  if (!rangeOf(doc) || !doc.body.contains(rangeOf(doc)!.startContainer)) placeCaretAtEnd(doc);
  const range = rangeOf(doc)!;
  range.deleteContents();
  const template = doc.createElement('template');
  template.innerHTML = html;
  let fragment: DocumentFragment = template.content;
  const top = [...fragment.childNodes].filter(
    (node) => node.nodeType !== Node.TEXT_NODE || node.textContent?.trim()
  );
  const container = elementOf(range.startContainer)?.closest(PHRASING_BLOCKS);
  // A single plain paragraph joins the paragraph the caret is in, the way
  // pasted text does. Between blocks (appending at the end) it stays a
  // paragraph of its own, attributes included.
  if (
    container &&
    top.length === 1 &&
    (top[0] as Element).localName === 'p' &&
    !(top[0] as Element).attributes.length &&
    ![...top[0].childNodes].some(isBlock)
  ) {
    const inline = doc.createDocumentFragment();
    inline.append(...top[0].childNodes);
    fragment = inline;
  }
  const inserted = [...fragment.childNodes];
  if (!inserted.length) return [];
  if (inserted.some(isBlock) && container && doc.body.contains(container)) {
    const tail = doc.createRange();
    tail.setStart(range.startContainer, range.startOffset);
    tail.setEnd(container, container.childNodes.length);
    const after = container.cloneNode(false) as Element;
    after.append(tail.extractContents());
    container.after(after);
    after.before(fragment);
    if (isEmptyBlock(container)) container.remove();
    if (isEmptyBlock(after) && after.nextSibling) after.remove();
    else if (isEmptyBlock(after)) after.innerHTML = '<br>';
  } else {
    range.insertNode(fragment);
  }
  const last = inserted[inserted.length - 1];
  const caret = doc.createRange();
  if (isBlock(last) && last.nextSibling) caret.setStart(last.nextSibling, 0);
  else caret.setStartAfter(last);
  caret.collapse(true);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(caret);
  return inserted;
}

/**
 * Splits a tab span at a point inside its text, so the point falls between two
 * spans (a pasted Word or Mail tab, or two tabs the browser merged, is one
 * span of several tab characters). Returns the span the point now sits
 * before: the span itself for a point at its start (nothing is split), the new
 * span holding the rest otherwise — or null for a point at its end.
 */
function splitTabSpan(span: Element, node: Node, offset: number): Element | null {
  const doc = span.ownerDocument;
  const tail = doc.createRange();
  tail.setStart(node, offset);
  tail.setEnd(span, span.childNodes.length);
  if (!tail.toString()) return null;
  const head = doc.createRange();
  head.setStart(span, 0);
  head.setEnd(node, offset);
  if (!head.toString()) return span;
  const rest = span.cloneNode(false) as Element;
  rest.append(tail.extractContents());
  span.after(rest);
  return rest;
}

/**
 * Moves a caret (or a selection's ends) out of a tab character's span — behind
 * it, or before it at its very start — so that inserting a tab never nests one
 * span in another (BLP/nx#3328). The span is the one the editor inserts. A
 * point inside the text of a span of several tabs splits it there: only the
 * tabs the selection covers are replaced.
 */
export function leaveTabSpan(doc: Document): void {
  const range = rangeOf(doc);
  if (!range) return;
  const tabOf = (node: Node) => elementOf(node)?.closest('span.Apple-tab-span') ?? null;
  const startSpan = tabOf(range.startContainer);
  const endSpan = tabOf(range.endContainer);
  if (!startSpan && !endSpan) return;
  const { startContainer, startOffset, endContainer, endOffset } = range;
  const next = doc.createRange();
  if (range.collapsed) {
    const rest = splitTabSpan(startSpan!, startContainer, startOffset);
    if (rest) next.setStartBefore(rest);
    else next.setStartAfter(startSpan!);
    next.collapse(true);
  } else {
    // A point outside a span in an element shifts when a split adds a node:
    // it is kept as the child it sits before, not as an offset.
    const pin = (node: Node, offset: number) => ({ node, before: node.childNodes[offset] ?? null });
    const unpin = (point: { node: Node; before: Node | null }): [Node, number] => [
      point.node,
      point.before
        ? Array.prototype.indexOf.call(point.node.childNodes, point.before)
        : point.node.childNodes.length,
    ];
    const startPin = !startSpan && startContainer.nodeType === Node.ELEMENT_NODE;
    const endPin = !endSpan && endContainer.nodeType === Node.ELEMENT_NODE;
    const startAt = startPin ? pin(startContainer, startOffset) : undefined;
    const endAt = endPin ? pin(endContainer, endOffset) : undefined;
    // The end first: the start's node and offset stay valid in a shared text node.
    const endRest = endSpan ? splitTabSpan(endSpan, endContainer, endOffset) : null;
    const startRest = startSpan ? splitTabSpan(startSpan, startContainer, startOffset) : null;
    if (startSpan) {
      if (startRest) next.setStartBefore(startRest);
      else next.setStartAfter(startSpan);
    } else if (startAt) next.setStart(...unpin(startAt));
    else next.setStart(startContainer, startOffset);
    if (endSpan) {
      // An end at the span's end follows the last piece the start's split left of it.
      if (endRest) next.setEndBefore(endRest);
      else next.setEndAfter(startRest && startSpan === endSpan ? startRest : endSpan);
    } else if (endAt) next.setEnd(...unpin(endAt));
    else next.setEnd(endContainer, endOffset);
  }
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(next);
}

/** Puts the caret into the first cell of a table. */
export function caretIntoFirstCell(doc: Document, nodes: readonly Node[]): void {
  const table = nodes.find((node) => (node as Element).localName === 'table') as
    | HTMLTableElement
    | undefined;
  const cell = table?.querySelector('td, th');
  if (cell) placeCaret(doc, cell);
}

/**
 * Puts the caret behind the body's last block — between blocks, where an
 * appended paragraph stays one of its own. Not for typing: Firefox types
 * there outside every paragraph (see `placeCaretAtTextEnd`).
 */
export function placeCaretAtEnd(doc: Document): void {
  const range = doc.createRange();
  range.selectNodeContents(doc.body);
  range.collapse(false);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** What lies between a table or a figure and the text in it. */
const TEXT_HOLDERS = new Set([
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'figcaption',
]);

/** Source whitespace between blocks; a no-break space is text. */
const isLayoutSpace = (node: Node) =>
  node.nodeType === Node.TEXT_NODE && /^[ \t\n\f\r]*$/.test(node.textContent ?? '');

/**
 * The last child that shows text: no whitespace between blocks, no comment
 * (the editor keeps Outlook's conditional ones and a signature's markers), no
 * closing rule.
 */
function lastContent(node: Node): Node | null {
  let child = node.lastChild;
  while (
    child &&
    (isLayoutSpace(child) ||
      (child.nodeType !== Node.TEXT_NODE && child.nodeType !== Node.ELEMENT_NODE) ||
      (child as Element).localName === 'hr')
  )
    child = child.previousSibling;
  return child;
}

/**
 * Puts the caret where typing continues the text: the end of the last block,
 * before a closing `<br>` (the placeholder of an empty or closing line, also
 * at the end of a closing inline) and before a `<pre>`'s closing line feed. It
 * goes down through containers — a quote, a list into its last item, a table
 * into its last cell — and changes nothing: a document ending in a list or a
 * table goes on in its last item or cell rather than in a paragraph made up
 * on focus; one ending in a rule goes on in the text above it. Chromium moves
 * a caret behind the last block into it on its own, Firefox types there
 * outside every paragraph (BLP/nx#3318).
 */
export function placeCaretAtTextEnd(doc: Document): void {
  let container: Node = doc.body;
  let last = lastContent(container);
  while (last && (isBlock(last) || TEXT_HOLDERS.has((last as Element).localName))) {
    container = last;
    last = lastContent(container);
  }
  let end = last;
  while (end?.nodeType === Node.ELEMENT_NODE && (end as Element).localName !== 'br')
    end = lastContent(end);
  const range = doc.createRange();
  if (end && (end as Element).localName === 'br') {
    range.setStartBefore(end);
  } else if (
    end?.nodeType === Node.TEXT_NODE &&
    (end === last || end.textContent!.endsWith('\n'))
  ) {
    const text = end.textContent!;
    const feed = text.endsWith('\n') && !!end.parentElement?.closest('pre');
    range.setStart(end, text.length - (feed ? 1 : 0));
  } else {
    range.setStart(container, container.childNodes.length);
  }
  range.collapse(true);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** Moves the caret to the next or previous table cell; false when there is none. */
export function moveToCell(doc: Document, direction: 1 | -1): boolean {
  const cell = closestIn(doc, 'td, th');
  const table = cell?.closest('table');
  if (!cell || !table) return false;
  const cells = [...table.querySelectorAll<HTMLTableCellElement>('td, th')].filter(
    (c) => c.closest('table') === table
  );
  const next = cells[cells.indexOf(cell as HTMLTableCellElement) + direction];
  if (!next) return false;
  const range = doc.createRange();
  range.selectNodeContents(next);
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  return true;
}
